import React, { useEffect, useState } from 'react'
import { VendorSubmission } from './VendorSubmission'
import { useCompany } from '../../../../hooks/useCompany'
import { useServiceContract } from '../../../../hooks/useServiceContract'
import { useTheme } from '@mui/material/styles'
import {
    AreaConfig,
    ListVendor,
    ServiceContract,
    UnitConfig,
    getScCompoundKeyString,
    PricingContracts,
    PricingContractStatus,
} from '../../../../models'
import { Container, EzosLoader } from '../../../../components'
import { VendorPricing } from './VendorPricing'
import { usePricing } from '../../../../hooks/usePricing'

interface InputState {
    [compoundKey: string]: number | null
}

export interface DisplayState {
    ezosLoader: boolean
    requestVendorPricing: boolean
    vendorSubmission: boolean
    awaitingVendor: boolean
}

interface Props {
    vendor: ListVendor
    unitConfigs: UnitConfig[]
    areaConfigs: AreaConfig[]
    onClickRequestUpdatedPricing: (vendor: ListVendor) => void
    onAcceptPricing?: (vendorId: number) => void
    onRejectPricing?: (vendorId: number) => void
}

export const PricingTab = (props: Props) => {
    const {
        vendor,
        unitConfigs,
        areaConfigs,
        onClickRequestUpdatedPricing,
        onAcceptPricing,
        onRejectPricing,
    } = props
    const theme = useTheme()
    const [displayState, setDisplayState] = useState<DisplayState>({
        ezosLoader: true,
        requestVendorPricing: false,
        vendorSubmission: false,
        awaitingVendor: false,
    })

    // State for current and new (vendor) service contracts
    const [currentScMap, setCurrentScMap] = useState<InputState>({})
    const [vendorScMap, setVendorScMap] = useState<InputState>({})
    const { getServiceContractList } = useServiceContract()
    const {
        getVendorServiceContractList,
        vendorServiceContractList,
        acceptNewPricing,
        rejectNewPricing,
    } = useCompany()
    const {
        pricingContracts,
        fetchPricingContracts,
        generatePricingContractPDF,
    } = usePricing()

    useEffect(() => {
        fetchPricingContracts(vendor.id)
    }, [vendor.id])
    // Fetch both sets of data
    useEffect(() => {
        getServiceContractList({ params: { vendor: vendor.id } }).then(
            (res) => {
                const mapping: InputState = {}
                res.data.forEach((sc: ServiceContract) => {
                    mapping[getScCompoundKeyString(sc)] = sc.price
                })
                setCurrentScMap(mapping)
            },
        )
        getVendorServiceContractList(vendor.id)
    }, [vendor.id])

    useEffect(() => {
        if (vendorServiceContractList) {
            const mapping: InputState = {}
            vendorServiceContractList.forEach((sc: ServiceContract) => {
                mapping[getScCompoundKeyString(sc)] = sc.price
            })
            setVendorScMap(mapping)
        }
    }, [vendorServiceContractList])

    const determineDisplayState = () => {
        if (pricingContracts.length === 0) {
            setDisplayState({
                ezosLoader: true,
                requestVendorPricing: false,
                vendorSubmission: false,
                awaitingVendor: false,
            })
            return
        }
        // if awaiting review is a key in the pricingContracts object, then display the vendor submission
        if (
            pricingContracts.find(
                (pc: PricingContracts) =>
                    pc.status === PricingContractStatus.AWAITING_REVIEW,
            )
        ) {
            setDisplayState({
                ezosLoader: false,
                requestVendorPricing: false,
                vendorSubmission: true,
                awaitingVendor: false,
            })
            return
        }
        // if awaiting vendor is a key in the pricingContracts object, then display the vendor submission
        if (
            pricingContracts.find(
                (pc: PricingContracts) =>
                    pc.status === PricingContractStatus.AWAITING_VENDOR,
            )
        ) {
            setDisplayState({
                ezosLoader: false,
                requestVendorPricing: false,
                vendorSubmission: false,
                awaitingVendor: true,
            })
            return
        }
        // we should always have a current pricing contract
        // no need to check for it, if we don't then the next
        // step would be to request one
        setDisplayState({
            ezosLoader: false,
            requestVendorPricing: true,
            vendorSubmission: false,
            awaitingVendor: false,
        })
    }

    useEffect(() => {
        determineDisplayState()
    }, [pricingContracts])

    const handleGeneratePricingContractPDF = () => {
        generatePricingContractPDF(vendor.id)
    }

    const handleAcceptNewPricing = async () => {
        await acceptNewPricing(vendor.id)
        await fetchPricingContracts(vendor.id)
        setDisplayState({
            ezosLoader: false,
            requestVendorPricing: true,
            vendorSubmission: false,
            awaitingVendor: false,
        })
        if (onAcceptPricing) {
            onAcceptPricing(vendor.id)
        }
    }

    const handleRejectNewPricing = async () => {
        await rejectNewPricing(vendor.id)
        await fetchPricingContracts(vendor.id)
        setDisplayState({
            ezosLoader: false,
            requestVendorPricing: true,
            vendorSubmission: false,
            awaitingVendor: false,
        })
    }

    return (
        <Container
            direction="column"
            style={{ height: '100%', width: '100%', overflowY: 'auto' }}
        >
            {displayState.ezosLoader && <EzosLoader />}
            {(displayState.requestVendorPricing ||
                displayState.awaitingVendor) && (
                <VendorPricing
                    vendor={vendor}
                    onClickRequestUpdatedPricing={onClickRequestUpdatedPricing}
                    pricingContracts={pricingContracts}
                    displayState={displayState}
                    setDisplayState={setDisplayState}
                    handleGeneratePricingContractPDF={
                        handleGeneratePricingContractPDF
                    }
                />
            )}
            {displayState.vendorSubmission && (
                <VendorSubmission
                    vendor={vendor}
                    unitConfigs={unitConfigs}
                    areaConfigs={areaConfigs}
                    currentScMap={currentScMap}
                    vendorScMap={vendorScMap}
                    handleAcceptNewPricing={() => {
                        handleAcceptNewPricing()
                    }}
                    handleRejectNewPricing={() => {
                        handleRejectNewPricing()
                        onRejectPricing?.(vendor.id)
                    }}
                />
            )}
        </Container>
    )
}
